/**
 * APP PROMOTION WORKSPACE — /google-ads/app-promotion/create
 *
 * Same three-step shape as the YouTube workspace (step rail · top strip ·
 * sticky preview), tuned to Google App campaigns:
 *
 *   1. Campaign settings — store + app id (paste a Play / App Store URL),
 *      bidding goal, target CPA, daily budget, dates, locations, languages.
 *   2. Ad group & ad — App campaigns hold exactly ONE ad group; the ad is
 *      2–5 headlines, 1–5 descriptions, images (uploaded → asset) and
 *      YouTube videos (→ asset).
 *   3. Review & save — a live build log: campaign → ad group → each asset
 *      → ad. Resumable: created ids are remembered by key, so a Google
 *      rejection can be fixed and re-published without duplicating anything.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { MarketingApp, marketingAppsService } from '@/services/marketing-apps.service';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { EmptyState } from '@/components/EmptyState';
import toast from 'react-hot-toast';
import {
  Check, CheckCircle2, ChevronRight, Circle, ClipboardPaste, Film, Globe, Image as ImageIcon, Layers, Plus,
  Settings, Sparkles, Trash2, Upload, Wand2, X, XCircle,
} from 'lucide-react';
import { Modal } from '@/components/Modal';
import {
  googleAdsService, AppPromotionAiApp, AppPromotionAiEnhanceVariant, AppPromotionAiGoal, GoogleAdsAppBiddingGoal, GoogleAdsAppStore,
  GoogleAdsGeoSuggestion, GoogleAdsLanguage,
} from '@/services/google-ads.service';
import { AiAssistantPanel } from '@/components/ai-assistant/AiAssistantPanel';
import { AppPromotionAiField, AppPromotionAiForm, createAppPromotionPlaybook } from '@/components/ai-assistant/playbooks/appPromotionPlaybook';
import { LibraryFile, MEDIA_FOLDERS, uploadsService } from '@/services/uploads.service';
import { MediaLibraryModal } from '@/components/MediaLibraryModal';
import { parseYoutubeId } from '@/pages/google-ads/googleAdsTargeting';
import {
  ErrorBox, errMsg, Label, Section, Spinner, Tag, useYoutubeAuth,
} from '@/components/google-ads/youtube/youtubeShared';
import { LanguagePicker, LocationSearch } from '@/components/google-ads/youtube/TargetingPickers';
import {
  APP_AD_LIMITS, APP_BASE, APP_GOALS, APP_STORES, AppAdPreview, conformAppImage, goalMeta, isValidAppId, parseStoreInput, storeLabel,
} from '@/components/google-ads/app-promotion/appPromotionShared';

const STEPS = [
  { key: 'campaign', label: 'Campaign settings', icon: Settings },
  { key: 'creative', label: 'Ad group & ad', icon: Layers },
  { key: 'review', label: 'Review & save', icon: CheckCircle2 },
] as const;
type StepKey = (typeof STEPS)[number]['key'];

const today = () => new Date().toISOString().slice(0, 10);
const toMicros = (v: string) => Math.round(Number(v) * 1_000_000);
const uid = () => Math.random().toString(36).slice(2, 10);

type ImageDraft = { id: string; url: string; note: string | null; width?: number; height?: number; shape?: string; cropped?: boolean; problem?: string };

/** One line per list entry so a long list of short strings stays scannable. */
function TextList({ items, onChange, max, maxChars, placeholder, label }: {
  items: string[]; onChange: (next: string[]) => void; max: number; maxChars: number; placeholder: string; label: string;
}) {
  return (
    <div className="space-y-2">
      {items.map((v, i) => (
        <div key={i} className="flex items-center gap-2">
          <Input value={v} maxLength={maxChars} placeholder={placeholder}
            onChange={(e) => onChange(items.map((x, j) => (j === i ? e.target.value : x)))} aria-label={`${label} ${i + 1}`} />
          <span className={`w-12 shrink-0 text-right text-2xs ${v.length > maxChars - 5 ? 'text-warning' : 'text-text-muted'}`}>{v.length}/{maxChars}</span>
          <button type="button" className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-text-muted hover:bg-surface-alt hover:text-danger"
            aria-label={`Remove ${label} ${i + 1}`} disabled={items.length <= 1}
            onClick={() => onChange(items.filter((_, j) => j !== i))}>
            <Trash2 size={14} />
          </button>
        </div>
      ))}
      {items.length < max && (
        <Button variant="secondary" size="sm" iconLeft={<Plus size={13} />} onClick={() => onChange([...items, ''])}>
          Add {label.toLowerCase()}
        </Button>
      )}
    </div>
  );
}

/** Marks a value the assistant filled in; disappears the moment the user edits the field. */
const AiBadge = ({ on }: { on: boolean }) => on ? (
  <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-primary-soft px-1.5 py-0.5 align-middle text-[10px] font-medium text-primary">
    <Sparkles size={9} /> AI suggested
  </span>
) : null;

const ENHANCE_VARIANTS: Array<{ value: AppPromotionAiEnhanceVariant; label: string; hint: string }> = [
  { value: 'clean_background', label: 'Clean background', hint: 'Same subject on a soft studio backdrop (1:1).' },
  { value: 'landscape_1_91', label: 'Landscape', hint: 'Recomposed wide for feed and Display placements (16:9).' },
  { value: 'square_1_1', label: 'Square', hint: 'Recomposed 1:1 for Play and Discover.' },
  { value: 'device_frame', label: 'On a phone', hint: 'Screenshot placed inside a phone frame (4:5).' },
];

/** One labelled value on the review screen. */
const Fact = ({ label, value, sub, mono, wide }: { label: string; value: string; sub?: string; mono?: boolean; wide?: boolean }) => (
  <div className={wide ? 'sm:col-span-2' : undefined}>
    <div className="text-2xs font-semibold uppercase tracking-wide text-text-muted">{label}</div>
    <div className={`mt-0.5 break-words text-[13px] font-medium text-text-primary ${mono ? 'font-mono text-xs' : ''}`}>{value}</div>
    {sub && <div className="text-2xs text-text-muted">{sub}</div>}
  </div>
);

export default function AppPromotionWizard() {
  const [params] = useSearchParams(); const { user } = useAuth();
  const registryAppUid = params.get('appUid');
  return <AppPromotionForm key={`${user?.companyUid}:${registryAppUid || 'manual'}`} registryAppUid={registryAppUid} />;
}

function AppPromotionForm({ registryAppUid }: { registryAppUid: string | null }) {
  const navigate = useNavigate();
  const { auth, accountUid } = useYoutubeAuth();
  const [registryApp, setRegistryApp] = useState<MarketingApp | null>(null);
  const [registryError, setRegistryError] = useState('');
  const [registryLoading, setRegistryLoading] = useState(!!registryAppUid);
  const accountReady = !!(auth?.connected && auth?.customer_selected && accountUid);
  const ready = accountReady && (!registryAppUid || !!registryApp && !registryLoading && !registryError);
  const currency = auth?.currency || '';

  const [step, setStep] = useState<StepKey>('campaign');

  // ── campaign settings ──
  const [store, setStore] = useState<GoogleAdsAppStore>('GOOGLE_APP_STORE');
  const [appId, setAppId] = useState('');
  const [storePaste, setStorePaste] = useState('');
  const [pasteNote, setPasteNote] = useState<string | null>(null);
  const [name, setName] = useState(`App - Installs - ${today()}`);
  const [goal, setGoal] = useState<GoogleAdsAppBiddingGoal>('OPTIMIZE_INSTALLS_TARGET_INSTALL_COST');
  const [targetCpa, setTargetCpa] = useState('');
  const [budget, setBudget] = useState('');
  // Blank = starts when the campaign is enabled. An explicit "today" is already in the past for
  // Google Ads accounts ahead of UTC and gets rejected (CANNOT_SET_DATE_TO_PAST).
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [locations, setLocations] = useState<GoogleAdsGeoSuggestion[]>([]);
  const [languages, setLanguages] = useState<GoogleAdsLanguage[]>([]);
  useEffect(() => {
    if (!registryAppUid) return;
    let current = true;
    marketingAppsService.get(registryAppUid).then(app => {
      if (!current) return;
      if (!app.readiness.google.detailsReady || !app.googleAppIdentifier) throw new Error('Complete the active app’s store URL and Google Ads identifier first.');
      setRegistryApp(app); setStore(app.platform === 'ios' ? 'APPLE_APP_STORE' : 'GOOGLE_APP_STORE');
      setAppId(app.platform === 'android' ? app.androidPackageName! : app.googleAppIdentifier);
      setStorePaste((app.platform === 'android' ? app.playStoreUrl : app.appStoreUrl) || ''); setName(`${app.name} · App promotion`);
    }).catch(error => { if (current) setRegistryError(error?.response ? 'Could not load this app in your company.' : error.message); })
      .finally(() => { if (current) setRegistryLoading(false); });
    return () => { current = false; };
  }, [registryAppUid]);

  // ── ad group + ad ──
  const [groupName, setGroupName] = useState('Ad group 1');
  const [headlines, setHeadlines] = useState<string[]>(['', '']);
  const [descriptions, setDescriptions] = useState<string[]>(['']);
  const [images, setImages] = useState<ImageDraft[]>([]);
  const [videos, setVideos] = useState<string[]>(['']);

  // ── AI assistant (App Promotion playbook only) ──
  const [aiOpen, setAiOpen] = useState(false);
  const [aiFields, setAiFields] = useState<Set<AppPromotionAiField>>(() => new Set());
  const [aiCtx, setAiCtx] = useState<{ sessionId: string; app: AppPromotionAiApp; goal?: AppPromotionAiGoal; language?: string } | null>(null);
  const aiCtxRef = useRef<{ sessionId: string; app: AppPromotionAiApp; goal?: AppPromotionAiGoal; language?: string } | null>(null);
  const [libraryOpen, setLibraryOpen] = useState(false);
  /** null = unknown/still loading; 0 = account has no in-app conversion actions → in-app goals would be refused. */
  const [inAppConversions, setInAppConversions] = useState<number | null>(null);
  useEffect(() => {
    let current = true; setInAppConversions(null);
    if (!accountUid || registryAppUid && !appId) return;
    googleAdsService.appPromotion.conversionReadiness(accountUid, appId.trim() || undefined)
      .then((r) => { if (current) setInAppConversions(r.inAppConversions); })
      .catch(() => { if (current) setInAppConversions(null); });
    return () => { current = false; };
  }, [accountUid, appId, registryAppUid]);
  const inAppBlocked = registryAppUid ? !(inAppConversions !== null && inAppConversions > 0) : inAppConversions === 0;
  // If a draft or an earlier choice landed on an in-app goal the account can't use, fall back.
  useEffect(() => {
    if (inAppBlocked && goal !== 'OPTIMIZE_INSTALLS_TARGET_INSTALL_COST') setGoal('OPTIMIZE_INSTALLS_TARGET_INSTALL_COST');
  }, [inAppBlocked, goal]);
  const [writingCopy, setWritingCopy] = useState(false);
  const [enhance, setEnhance] = useState<{ image: ImageDraft; variant: AppPromotionAiEnhanceVariant | null; busy: boolean; result: { url: string; width?: number; height?: number; label: string } | null; error: string | null } | null>(null);
  const isAi = (f: AppPromotionAiField) => aiFields.has(f);
  const clearAi = useCallback((...fields: AppPromotionAiField[]) => {
    setAiFields((prev) => {
      if (!fields.some((f) => prev.has(f))) return prev;
      const next = new Set(prev); fields.forEach((f) => next.delete(f)); return next;
    });
  }, []);
  const playbook = useMemo(() => createAppPromotionPlaybook({ accountUid, currency: currency || undefined }), [accountUid, currency]);
  const aiForm = useMemo<AppPromotionAiForm>(() => ({
    setStore: value => { if (!registryAppUid) setStore(value); }, setAppId: value => { if (!registryAppUid) setAppId(value); },
    setStorePaste: value => { if (!registryAppUid) setStorePaste(value); }, setName, setGoal, setTargetCpa, setBudget, setStartDate, setEndDate, setLocations, setLanguages,
    markAi: (fields) => setAiFields((prev) => new Set([...Array.from(prev), ...fields])),
    setAiContext: (ctx) => { aiCtxRef.current = ctx; setAiCtx(ctx); },
  }), [registryAppUid]);

  const writeWithAi = async (ctxOverride?: typeof aiCtxRef.current) => {
    const ctx = ctxOverride ?? aiCtxRef.current ?? aiCtx;
    setWritingCopy(true);
    try {
      const r = await googleAdsService.appPromotionAi.creatives({
        sessionId: ctx?.sessionId,
        app: { name: ctx?.app.name ?? undefined, description: ctx?.app.description ?? undefined, category: ctx?.app.category ?? undefined, store, appId: appId.trim() || undefined },
        goal: ctx?.goal,
        language: ctx?.language,
      });
      setHeadlines(r.headlines.length >= APP_AD_LIMITS.headlines.min ? r.headlines : [...r.headlines, ...Array(APP_AD_LIMITS.headlines.min - r.headlines.length).fill('')]);
      setDescriptions(r.descriptions.length ? r.descriptions : ['']);
      setAiFields((prev) => new Set<AppPromotionAiField>([...Array.from(prev), 'headlines', 'descriptions']));
      toast.success('Copy drafted — edit any line before publishing.');
    } catch (e) {
      if (!(e as { response?: unknown })?.response) toast.error(errMsg(e, 'Could not write the copy.'));
    } finally { setWritingCopy(false); }
  };

  const runEnhance = async (variant: AppPromotionAiEnhanceVariant) => {
    if (!enhance) return;
    setEnhance((p) => (p ? { ...p, variant, busy: true, result: null, error: null } : p));
    try {
      const r = await googleAdsService.appPromotionAi.enhanceImage(enhance.image.url, variant);
      setEnhance((p) => (p ? { ...p, busy: false, result: { url: r.url, width: r.width, height: r.height, label: r.label } } : p));
    } catch (e) {
      setEnhance((p) => (p ? { ...p, busy: false, error: errMsg(e, 'Enhancement failed.') } : p));
    }
  };
  const acceptEnhance = (mode: 'add' | 'replace') => {
    if (!enhance?.result) return;
    const { image, result } = enhance;
    const made: ImageDraft = { id: uid(), url: result.url, note: `${image.note ?? 'image'} · AI ${result.label.toLowerCase()}`, width: result.width, height: result.height };
    if (mode === 'replace') setImages((p) => p.map((i) => (i.id === image.id ? made : i)));
    else {
      if (images.length >= APP_AD_LIMITS.images.max) { toast.error(`Max ${APP_AD_LIMITS.images.max} images.`); return; }
      setImages((p) => [...p, made]);
    }
    setEnhance(null);
  };

  const applyPaste = (raw: string) => {
    setStorePaste(raw);
    clearAi('store', 'appId');
    const parsed = parseStoreInput(raw);
    if (!parsed) { setPasteNote(raw.trim() ? 'Not a Play / App Store URL or id.' : null); return; }
    setStore(parsed.store); setAppId(parsed.appId);
    setPasteNote(`${storeLabel(parsed.store)} · ${parsed.appId}`);
  };

  const uploadImage = async (file: File | null) => {
    if (!file) return;
    if (images.length >= APP_AD_LIMITS.images.max) { toast.error(`Max ${APP_AD_LIMITS.images.max} images.`); return; }
    const id = uid();
    setImages((p) => [...p, { id, url: '', note: 'Uploading…' }]);
    try {
      const kit = await uploadsService.imagekit(file, MEDIA_FOLDERS.googleAppPromotion);
      if (!kit.url) throw new Error('Upload returned no URL.');
      const fit = conformAppImage(kit.url, kit.width, kit.height);
      if (fit.problem) throw new Error(fit.problem);
      if (fit.cropped) toast(`${file.name}: cropped to ${fit.shape?.label} (${fit.width}×${fit.height}) — Google only takes 1.91:1, 1:1 or 4:5.`, { icon: '✂️' });
      setImages((p) => p.map((i) => (i.id === id ? { id, url: fit.url, note: file.name, width: fit.width, height: fit.height, shape: fit.shape?.label, cropped: fit.cropped } : i)));
    } catch (e) {
      const msg = errMsg(e);
      setImages((p) => p.filter((i) => i.id !== id));
      toast.error(`Image not added — ${msg}`);
    }
  };

  /** Reuse images already in the company library — no re-upload for the same app. */
  const addFromLibrary = (files: LibraryFile[]) => {
    setImages((p) => {
      const room = APP_AD_LIMITS.images.max - p.length;
      const fresh = files.filter((f) => !p.some((x) => x.url === f.url)).slice(0, Math.max(0, room));
      if (fresh.length < files.length) toast.error(fresh.length === 0 ? `Max ${APP_AD_LIMITS.images.max} images.` : `Added ${fresh.length} — max ${APP_AD_LIMITS.images.max} images.`);
      const made: ImageDraft[] = [];
      for (const f of fresh) {
        const fit = conformAppImage(f.url, f.width, f.height);
        if (fit.problem) { toast.error(`${f.name}: ${fit.problem}`); continue; }
        if (fit.cropped) toast(`${f.name}: cropped to ${fit.shape?.label} (${fit.width}×${fit.height}).`, { icon: '✂️' });
        made.push({ id: uid(), url: fit.url, note: f.name, width: fit.width, height: fit.height, shape: fit.shape?.label, cropped: fit.cropped });
      }
      return [...p, ...made];
    });
  };

  const validVideos = useMemo(() => videos.map((v) => parseYoutubeId(v)).filter((v): v is string => !!v), [videos]);
  const badVideos = useMemo(() => videos.filter((v) => v.trim() && !parseYoutubeId(v)), [videos]);
  const cleanHeadlines = useMemo(() => headlines.map((h) => h.trim()).filter(Boolean), [headlines]);
  const cleanDescriptions = useMemo(() => descriptions.map((d) => d.trim()).filter(Boolean), [descriptions]);
  const readyImages = useMemo(() => images.filter((i) => i.url), [images]);

  // ── validation ──
  const campaignIssues = useMemo(() => {
    const i: string[] = [];
    if (!appId.trim()) i.push('Enter the app id (or paste the store URL).');
    else if (!isValidAppId(store, appId)) i.push(store === 'GOOGLE_APP_STORE' ? 'Play package names look like com.example.app.' : 'App Store ids are the digits after "id" in the store URL.');
    if (!name.trim()) i.push('Campaign name is required.');
    if (!targetCpa || Number(targetCpa) <= 0) i.push(`Enter a ${goalMeta(goal)?.cpaLabel.toLowerCase() ?? 'target CPA'}.`);
    if (!budget || Number(budget) <= 0) i.push('Enter a daily budget.');
    if (budget && targetCpa && Number(budget) < Number(targetCpa) * 10) i.push('Google recommends a daily budget of at least 10× your target CPA — below that the campaign rarely leaves learning.');
    if (endDate && endDate < startDate) i.push('End date is before the start date.');
    return i;
  }, [appId, store, name, targetCpa, budget, startDate, endDate, goal]);

  const creativeIssues = useMemo(() => {
    const i: string[] = [];
    if (!groupName.trim()) i.push('Ad group name is required.');
    if (cleanHeadlines.length < APP_AD_LIMITS.headlines.min) i.push(`Add at least ${APP_AD_LIMITS.headlines.min} headlines (${APP_AD_LIMITS.headlines.chars} characters each).`);
    if (new Set(cleanHeadlines).size !== cleanHeadlines.length) i.push('Headlines must be different from each other.');
    if (cleanDescriptions.length < APP_AD_LIMITS.descriptions.min) i.push(`Add at least ${APP_AD_LIMITS.descriptions.min} description (${APP_AD_LIMITS.descriptions.chars} characters).`);
    if (badVideos.length) i.push(`${badVideos.length} video line${badVideos.length === 1 ? ' is' : 's are'} not a YouTube URL or id.`);
    if (images.some((im) => !im.url)) i.push('An image is still uploading.');
    return i;
  }, [groupName, cleanHeadlines, cleanDescriptions, badVideos, images]);

  const allIssues = [...campaignIssues, ...creativeIssues];
  const issuesFor = (k: StepKey) => (k === 'campaign' ? campaignIssues : k === 'creative' ? creativeIssues : []);

  // ── publish (resumable) ──
  type P = 'idle' | 'running' | 'done' | 'failed';
  const [pub, setPub] = useState<Record<string, P>>({});
  const [pubError, setPubError] = useState<string | null>(null);
  const [publishing, setPublishing] = useState(false);
  const madeRef = useRef<{ campaign?: string; targetingDone?: boolean; adGroup?: string; assets: Record<string, string>; ad?: string }>({ assets: {} });

  const publish = useCallback(async () => {
    if (!ready) return;
    setPublishing(true); setPubError(null);
    const mark = (k: string, v: P) => setPub((m) => ({ ...m, [k]: v }));
    try {
      const made = madeRef.current;
      if (!made.campaign) {
        mark('campaign', 'running');
        try {
          const res = await googleAdsService.appPromotion.createCampaign(accountUid, {
            registryAppUid: registryAppUid || undefined,
            name: name.trim(),
            appId: appId.trim(),
            appStore: store,
            biddingStrategyGoalType: goal,
            targetCpaMicros: toMicros(targetCpa),
            dailyBudgetMicros: toMicros(budget),
            status: 'PAUSED',
            ...(startDate ? { startDate } : {}),
            ...(endDate ? { endDate } : {}),
            geoTargetIds: locations.length ? locations.map((l) => l.id) : undefined,
            languageIds: languages.length ? languages.map((l) => l.id) : undefined,
          });
          made.campaign = res.campaignId;
          made.targetingDone = !res.targeting?.error;
          if (res.targeting?.error) throw new Error(`Campaign saved paused. Targeting needs correction: ${res.targeting.error}`);
        } catch (e) { mark('campaign', 'failed'); setPubError(errMsg(e)); return; }
        mark('campaign', 'done');
      }

      if (!made.targetingDone) {
        mark('campaign', 'running');
        try {
          await googleAdsService.addCampaignCriteria(accountUid, made.campaign!, { geoTargetIds: locations.map((l) => l.id), languageIds: languages.map((l) => l.id) });
          made.targetingDone = true; mark('campaign', 'done');
        } catch (e) { mark('campaign', 'failed'); setPubError(`Targeting: ${errMsg(e)}`); return; }
      }
      if (!made.adGroup) {
        mark('adGroup', 'running');
        try {
          const res = await googleAdsService.appPromotion.createAdGroup(accountUid, made.campaign, { name: groupName.trim(), status: 'PAUSED' });
          made.adGroup = res.adGroupId;
        } catch (e) { mark('adGroup', 'failed'); setPubError(`Ad group: ${errMsg(e)}`); return; }
        mark('adGroup', 'done');
      }

      for (const im of readyImages) {
        const key = `img:${im.url}`;
        if (made.assets[key]) continue;
        mark(key, 'running');
        try {
          const res = await googleAdsService.appPromotion.createAsset(accountUid, { imageUrl: im.url, name: im.note ?? undefined });
          made.assets[key] = res.resourceName;
        } catch (e) { mark(key, 'failed'); setPubError(`Image ${im.note ?? ''}: ${errMsg(e)}`); return; }
        mark(key, 'done');
      }
      for (const v of validVideos) {
        const key = `vid:${v}`;
        if (made.assets[key]) continue;
        mark(key, 'running');
        try {
          const res = await googleAdsService.appPromotion.createAsset(accountUid, { youtubeVideoId: v });
          made.assets[key] = res.resourceName;
        } catch (e) { mark(key, 'failed'); setPubError(`Video ${v}: ${errMsg(e)}`); return; }
        mark(key, 'done');
      }

      if (!made.ad) {
        mark('ad', 'running');
        try {
          const res = await googleAdsService.appPromotion.createAd(accountUid, made.adGroup, {
            headlines: cleanHeadlines.slice(0, APP_AD_LIMITS.headlines.max),
            descriptions: cleanDescriptions.slice(0, APP_AD_LIMITS.descriptions.max),
            // Identical images dedupe to one Google asset — send each asset id once.
            images: Array.from(new Set(readyImages.map((im) => made.assets[`img:${im.url}`]).filter(Boolean))),
            youtubeVideos: Array.from(new Set(validVideos.map((v) => made.assets[`vid:${v}`]).filter(Boolean))),
            status: 'PAUSED',
          });
          made.ad = res.resourceName;
        } catch (e) { mark('ad', 'failed'); setPubError(`Ad: ${errMsg(e)}`); return; }
        mark('ad', 'done');
      }

      toast.success('App campaign saved paused. Review it before enabling delivery.');
      setTimeout(() => navigate(APP_BASE), 1200);
    } finally { setPublishing(false); }
  }, [accountUid, ready, registryAppUid, name, appId, store, goal, targetCpa, budget, startDate, endDate, locations, languages, groupName,
    readyImages, validVideos, cleanHeadlines, cleanDescriptions, navigate]);

  const StepIcon = ({ s }: { s?: P }) =>
    s === 'done' ? <CheckCircle2 size={15} className="shrink-0 text-success" />
    : s === 'running' ? <Spinner size={15} className="text-primary" />
    : s === 'failed' ? <XCircle size={15} className="shrink-0 text-danger" />
    : <Circle size={15} className="shrink-0 text-text-muted/40" />;

  const stepIdx = STEPS.findIndex((s) => s.key === step);
  const goalInfo = goalMeta(goal)!;
  const storeInfo = APP_STORES.find((s) => s.value === store)!;

  return (
    <>
      <Topbar
        title="New App campaign"
        breadcrumb="Google Ads / App Promotion"
        description="Review your store listing, bidding and creative, then save the campaign paused."
      />

      <div className="w-full px-7 py-7">
        {registryAppUid && <Card className="mb-4">
          <div className="flex flex-wrap items-center justify-between gap-2"><h2 className="font-semibold text-text-primary">{registryApp?.name || 'Saved app'}</h2><Link to={`/marketing/apps/${encodeURIComponent(registryAppUid)}`} className="text-sm text-primary">App details</Link></div>
          {registryLoading ? <p className="mt-2 text-sm text-text-muted">Loading app details…</p> : registryError ? <p role="alert" className="mt-2 text-sm text-danger">{registryError}</p> : <>
            <p className="mt-2 text-sm text-text-secondary">{registryApp?.platform === 'ios' ? 'iOS App Store ID' : 'Android package'}: {registryApp?.googleAppIdentifier}</p>
            <p className="mt-2 text-xs text-text-muted">Install attribution/tracking is not configured in this version. Existing Google conversion actions for this app: {inAppConversions === null ? 'unverified' : `${inAppConversions} in-app actions`}. Google verifies store availability and campaign eligibility when you save.</p>
          </>}
        </Card>}
        {auth === undefined && (
          <div className="flex min-h-[50vh] items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          </div>
        )}

        {auth !== undefined && !accountReady && (
          <EmptyState
            icon={<Globe size={36} className="text-text-muted" />}
            title="Connect a Google Ads account first"
            description="Authorise Google and choose a customer account, then come back to build App campaigns."
            action={<Link to="/google-ads/account"><Button iconLeft={<Globe size={14} />}>Connect Google Ads</Button></Link>}
          />
        )}

        {ready && (
        <div className="flex w-full gap-5 pb-12">
          {/* step rail */}
          <div className="hidden w-52 shrink-0 lg:block">
            <Card className="sticky top-24 p-3">
              {STEPS.map((s, i) => {
                const Icon = s.icon;
                const active = s.key === step;
                const done = i < stepIdx && issuesFor(s.key).length === 0;
                const bad = i < stepIdx && issuesFor(s.key).length > 0;
                return (
                  <button key={s.key} onClick={() => setStep(s.key)}
                    className={`mb-1 flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-sm transition-colors ${
                      active ? 'bg-primary-soft font-medium text-primary' : 'text-text-secondary hover:bg-surface-alt'}`}>
                    {done ? <CheckCircle2 size={16} className="shrink-0 text-success" />
                      : bad ? <XCircle size={16} className="shrink-0 text-danger" />
                      : <Icon size={16} className="shrink-0" />}
                    {s.label}
                  </button>
                );
              })}
              <div className="mt-3 space-y-0.5 border-t border-border px-3 pt-3 text-2xs text-text-muted">
                <div>1 ad group</div>
                <div>1 ad</div>
                <div>{cleanHeadlines.length} headline{cleanHeadlines.length === 1 ? '' : 's'} · {cleanDescriptions.length} description{cleanDescriptions.length === 1 ? '' : 's'}</div>
                <div>{readyImages.length} image{readyImages.length === 1 ? '' : 's'} · {validVideos.length} video{validVideos.length === 1 ? '' : 's'}</div>
              </div>
            </Card>
          </div>

          {/* main */}
          <div className="min-w-0 flex-1 space-y-4">
            <Card padded={false} className="flex flex-wrap items-center gap-x-5 gap-y-1 px-4 py-2.5 text-xs text-text-muted">
              <span>Goal: <span className="font-medium text-text-primary">{goalInfo.short}</span></span>
              <span>Type: <span className="font-medium text-text-primary">App campaign</span></span>
              <span>Served via: <span className="font-medium text-text-primary">Play · Search · YouTube · Display</span></span>
              <Tag>starts paused</Tag>
            </Card>

            {/* ── STEP 1: campaign ── */}
            {step === 'campaign' && (
              <>
                {aiOpen ? (
                  <AiAssistantPanel
                    playbook={playbook}
                    form={aiForm}
                    onClose={() => setAiOpen(false)}
                    onApplied={() => {
                      toast.success('Draft applied — writing the ad copy next.');
                      // The playbook has just stored the session/app in aiCtxRef; use it directly.
                      if (aiCtxRef.current) void writeWithAi(aiCtxRef.current);
                    }}
                  />
                ) : (
                  <div className="flex flex-wrap items-center gap-4 rounded-2xl border border-primary/30 bg-primary-soft/25 px-4 py-3.5">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary text-white"><Sparkles size={18} /></span>
                    <span className="min-w-[220px] flex-1">
                      <span className="block text-[15px] font-semibold text-text-primary">AI assistant for App campaigns</span>
                      <span className="block text-xs text-text-muted">Paste your store URL, answer four questions, and every field below gets filled in. You review everything; nothing publishes on its own.</span>
                    </span>
                    <Button onClick={() => setAiOpen(true)} iconLeft={<Sparkles size={14} />} className="shrink-0">Start with AI</Button>
                  </div>
                )}

                <Section title="Your app" hint="Paste the store listing URL — the store and id are filled in for you.">
                  <div className="space-y-4">
                    <div>
                      <Label>Store URL or app id</Label>
                      <Input className="mt-1.5" iconLeft={<ClipboardPaste size={14} />}
                        placeholder="https://play.google.com/store/apps/details?id=com.example.app  ·  https://apps.apple.com/…/id284882215"
                        value={storePaste} disabled={!!registryAppUid} onChange={(e) => applyPaste(e.target.value)} />
                      {pasteNote && (
                        <p className={`mt-1.5 text-2xs ${pasteNote.startsWith('Not') ? 'text-warning' : 'text-success'}`}>{pasteNote}</p>
                      )}
                    </div>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      {APP_STORES.map((s) => (
                        <button key={s.value} type="button" disabled={!!registryAppUid} onClick={() => { setStore(s.value); clearAi('store'); }}
                          className={`flex items-start gap-3 rounded-xl border p-3.5 text-left transition-colors ${
                            store === s.value ? 'border-primary bg-primary-soft/40' : 'border-border hover:bg-surface-alt'}`}>
                          <span className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${
                            store === s.value ? 'border-primary' : 'border-border-strong'}`}>
                            {store === s.value && <span className="h-2 w-2 rounded-full bg-primary" />}
                          </span>
                          <span>
                            <span className="block text-sm font-medium text-text-primary">{s.label}</span>
                            <span className="block text-2xs text-text-muted">{s.idLabel} · e.g. {s.example}</span>
                          </span>
                        </button>
                      ))}
                    </div>
                    <div>
                      <Label>{storeInfo.idLabel}<AiBadge on={isAi('appId')} /></Label>
                      <Input className="mt-1.5 font-mono" placeholder={storeInfo.example} value={appId} readOnly={!!registryAppUid} onChange={(e) => { setAppId(e.target.value); clearAi('appId'); }} />
                      <p className="mt-1 text-2xs text-text-muted">{storeInfo.idHint}. The app must be published (or in open testing) — Google reads the listing.</p>
                    </div>
                  </div>
                </Section>

                <Section title="Campaign name" right={<AiBadge on={isAi('name')} />}>
                  <Input value={name} onChange={(e) => { setName(e.target.value); clearAi('name'); }} maxLength={256} />
                </Section>

                <Section title="Bidding, budget and dates" hint="App campaigns bid on a target cost — Google's automation picks the placements.">
                  <div className="space-y-4">
                    <div>
                      <Label>Bidding goal<AiBadge on={isAi('goal')} /></Label>
                      <Select className="mt-1.5" value={goal} onChange={(e) => { setGoal(e.target.value as GoogleAdsAppBiddingGoal); clearAi('goal'); }}>
                        {APP_GOALS.map((g) => {
                          const needsConv = g.value !== 'OPTIMIZE_INSTALLS_TARGET_INSTALL_COST';
                          return (
                            <option key={g.value} value={g.value} disabled={needsConv && inAppBlocked}>
                              {g.label}{needsConv && inAppBlocked ? ' — needs in-app conversion tracking' : ''}
                            </option>
                          );
                        })}
                      </Select>
                      <p className="mt-1 text-2xs text-text-muted">{goalInfo.hint}</p>
                      {inAppBlocked && (
                        <p className="mt-1 text-2xs text-warning">
                          This Google Ads account has no in-app conversion actions, so Google only accepts the installs goal. Add app conversions (Firebase or an app-analytics partner) under Goals → Conversions to unlock the other two.
                        </p>
                      )}
                    </div>
                    {/* 2×2: money on one row, dates on the next — four across squeezed the
                        long CPA label onto two lines and pushed its input out of alignment. */}
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div>
                        <Label>{goalInfo.cpaLabel}{currency ? ` (${currency})` : ''}<AiBadge on={isAi('targetCpa')} /></Label>
                        <Input className="mt-1.5" type="number" min="0" step="0.01" placeholder="e.g. 2.50" value={targetCpa} onChange={(e) => { setTargetCpa(e.target.value); clearAi('targetCpa'); }} />
                      </div>
                      <div>
                        <Label>Daily budget{currency ? ` (${currency})` : ''}<AiBadge on={isAi('budget')} /></Label>
                        <Input className="mt-1.5" type="number" min="0" placeholder="e.g. 50" value={budget} onChange={(e) => { setBudget(e.target.value); clearAi('budget'); }} />
                      </div>
                      <div>
                        <Label>Start date<AiBadge on={isAi('startDate')} /></Label>
                        <Input className="mt-1.5" type="date" value={startDate} onChange={(e) => { setStartDate(e.target.value); clearAi('startDate'); }} />
                        <p className="mt-1 text-2xs text-text-muted">Blank = starts when enabled.</p>
                      </div>
                      <div>
                        <Label>End date <span className="font-normal text-text-muted">(optional)</span><AiBadge on={isAi('endDate')} /></Label>
                        <Input className="mt-1.5" type="date" value={endDate} min={startDate || undefined} onChange={(e) => { setEndDate(e.target.value); clearAi('endDate'); }} />
                      </div>
                    </div>
                  </div>
                </Section>

                <Section title="Locations" hint="Searched live from Google — countries, regions and cities. Empty = worldwide." right={<AiBadge on={isAi('locations')} />}>
                  <LocationSearch accountUid={accountUid} value={locations} onChange={(v) => { setLocations(v); clearAi('locations'); }} />
                </Section>

                <Section title="Languages" hint="Tick the common ones or search Google's full list. Empty = all languages." right={<AiBadge on={isAi('languages')} />}>
                  <LanguagePicker accountUid={accountUid} value={languages} onChange={(v) => { setLanguages(v); clearAi('languages'); }} />
                </Section>

                <div className="flex justify-end">
                  <Button onClick={() => setStep('creative')} iconRight={<ChevronRight size={15} />}>Next: Ad group & ad</Button>
                </div>
              </>
            )}

            {/* ── STEP 2: ad group + ad ── */}
            {step === 'creative' && (
              <>
                <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-primary/30 bg-primary-soft/40 px-4 py-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/15 text-primary"><Sparkles size={16} /></span>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-text-primary">
                      {isAi('headlines') ? 'AI drafted this ad — review every line below' : 'Write the ad with AI'}
                    </div>
                    <div className="text-2xs text-text-muted">
                      {aiCtx?.app.name ? `Headlines and descriptions for ${aiCtx.app.name}` : 'Headlines and descriptions from the store listing'}
                      {' · '}images come from your library or an upload (Google needs 1.91:1, 1:1 or 4:5 — store screenshots are too tall).
                    </div>
                  </div>
                  <Button size="sm" variant={isAi('headlines') ? 'secondary' : 'primary'} iconLeft={<Wand2 size={13} />} loading={writingCopy}
                    disabled={writingCopy || (!appId.trim() && !aiCtx)} title={!appId.trim() && !aiCtx ? 'Enter the app id (step 1) first' : undefined}
                    onClick={() => writeWithAi()}>
                    {isAi('headlines') ? 'Rewrite with AI' : 'Write with AI'}
                  </Button>
                </div>

                <Section title="Ad group" hint="App campaigns hold exactly one ad group — Google rejects a second.">
                  <Label>Ad group name</Label>
                  <Input className="mt-1.5" value={groupName} onChange={(e) => setGroupName(e.target.value)} />
                </Section>

                <Section title="Headlines" hint={`${APP_AD_LIMITS.headlines.min}–${APP_AD_LIMITS.headlines.max}, up to ${APP_AD_LIMITS.headlines.chars} characters each. Google mixes them per placement.`}
                  right={(
                    <span className="flex items-center gap-2">
                      <AiBadge on={isAi('headlines')} />
                      <Button variant="soft" size="sm" iconLeft={<Wand2 size={13} />} loading={writingCopy} disabled={writingCopy || (!appId.trim() && !aiCtx)}
                        title={!appId.trim() && !aiCtx ? 'Enter the app id (step 1) first' : undefined} onClick={() => writeWithAi()}>
                        Write with AI
                      </Button>
                    </span>
                  )}>
                  <TextList items={headlines} onChange={(v) => { setHeadlines(v); clearAi('headlines'); }} max={APP_AD_LIMITS.headlines.max} maxChars={APP_AD_LIMITS.headlines.chars}
                    placeholder="e.g. Order food in 2 taps" label="Headline" />
                </Section>

                <Section title="Descriptions" hint={`${APP_AD_LIMITS.descriptions.min}–${APP_AD_LIMITS.descriptions.max}, up to ${APP_AD_LIMITS.descriptions.chars} characters each.`}
                  right={<AiBadge on={isAi('descriptions')} />}>
                  <TextList items={descriptions} onChange={(v) => { setDescriptions(v); clearAi('descriptions'); }} max={APP_AD_LIMITS.descriptions.max} maxChars={APP_AD_LIMITS.descriptions.chars}
                    placeholder="e.g. Fresh meals from 300+ restaurants, delivered in under 30 minutes." label="Description" />
                </Section>

                <Section title="Images" hint="Google accepts three shapes: landscape 1.91:1 (1200×628), square 1:1 (1200×1200) and portrait 4:5 (1200×1500). Other shapes are cropped to the nearest one automatically. Optional but strongly recommended.">
                  {images.length > 0 && (
                    <div className="mb-3 grid grid-cols-3 gap-2 sm:grid-cols-4 lg:grid-cols-6">
                      {images.map((im) => (
                        <div key={im.id} className="group relative aspect-square overflow-hidden rounded-xl border border-border bg-surface-alt">
                          {im.url
                            ? <img src={im.url} alt="" className="h-full w-full object-cover" />
                            : <span className="flex h-full w-full items-center justify-center text-text-muted"><Spinner size={16} /></span>}
                          {im.width && im.height && (
                            <span className="absolute bottom-1 left-1 rounded bg-black/70 px-1 py-0.5 text-[9px] text-white">
                              {im.shape ?? '?'}{im.cropped ? ' · cropped' : ''} · {im.width}×{im.height}
                            </span>
                          )}
                          <button type="button" aria-label="Remove image" title="Remove image"
                            className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-black/70 text-white shadow hover:bg-danger"
                            onClick={() => setImages((p) => p.filter((x) => x.id !== im.id))}>
                            <X size={12} />
                          </button>
                          {im.url && (
                            <button type="button" aria-label="Enhance with AI" title="Enhance with AI"
                              className="absolute left-1 top-1 flex h-6 items-center gap-1 rounded-full bg-black/60 px-1.5 text-[10px] text-white opacity-0 transition-opacity group-hover:opacity-100"
                              onClick={() => setEnhance({ image: im, variant: null, busy: false, result: null, error: null })}>
                              <Sparkles size={11} /> Enhance
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex flex-wrap gap-2">
                    <button type="button" onClick={() => setLibraryOpen(true)} disabled={images.length >= APP_AD_LIMITS.images.max}
                      className="flex items-center gap-2 rounded-lg border border-border bg-surface px-3 py-2.5 text-xs font-medium text-text-primary hover:border-primary/50 hover:bg-surface-alt disabled:opacity-50">
                      <ImageIcon size={14} className="shrink-0" />
                      Choose from library
                    </button>
                    <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-border px-3 py-2.5 text-xs text-text-muted hover:bg-surface-alt">
                      <Upload size={14} className="shrink-0" />
                      <span>Upload new… ({images.length}/{APP_AD_LIMITS.images.max})</span>
                      <input type="file" accept="image/png,image/jpeg" multiple className="hidden"
                        onChange={(e) => { Array.from(e.target.files ?? []).forEach((f) => uploadImage(f)); e.currentTarget.value = ''; }} />
                    </label>
                  </div>
                  <p className="mt-2 text-2xs text-text-muted">Uploads are saved to your workspace library, so the same app's images are one click away next time.</p>
                  <MediaLibraryModal open={libraryOpen} onClose={() => setLibraryOpen(false)} folder={MEDIA_FOLDERS.googleAppPromotion} type="image"
                    title="App images in your library" onPick={addFromLibrary}
                    validate={(f) => (f.width && f.height && (f.width < 320 || f.height < 320) ? `${f.width}×${f.height} is too small — Google wants at least 320px on the short side` : null)} />
                </Section>

                <Section title="YouTube videos" hint="Optional. Videos must already be on YouTube — paste the URLs; portrait, square and landscape all help.">
                  <div className="space-y-2">
                    {videos.map((v, i) => {
                      const vid = parseYoutubeId(v);
                      return (
                        <div key={i} className="flex items-center gap-2">
                          {vid
                            ? <img src={`https://i.ytimg.com/vi/${vid}/default.jpg`} alt="" className="h-8 w-14 shrink-0 rounded object-cover" />
                            : <span className="flex h-8 w-14 shrink-0 items-center justify-center rounded bg-surface-alt text-text-muted"><Film size={13} /></span>}
                          <Input placeholder="https://www.youtube.com/watch?v=…" value={v}
                            onChange={(e) => setVideos((p) => p.map((x, j) => (j === i ? e.target.value : x)))} aria-label={`Video ${i + 1}`} />
                          <button type="button" className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-text-muted hover:bg-surface-alt hover:text-danger"
                            aria-label={`Remove video ${i + 1}`} disabled={videos.length <= 1}
                            onClick={() => setVideos((p) => p.filter((_, j) => j !== i))}>
                            <Trash2 size={14} />
                          </button>
                        </div>
                      );
                    })}
                    {videos.length < APP_AD_LIMITS.videos.max && (
                      <Button variant="secondary" size="sm" iconLeft={<Plus size={13} />} onClick={() => setVideos((p) => [...p, ''])}>Add video</Button>
                    )}
                  </div>
                </Section>

                <div className="flex justify-between">
                  <Button variant="secondary" onClick={() => setStep('campaign')}>Back</Button>
                  <Button onClick={() => setStep('review')} iconRight={<ChevronRight size={15} />}>Next: Review</Button>
                </div>
              </>
            )}

            {/* ── STEP 3: review ── */}
            {step === 'review' && (
              <>
                <div className="grid grid-cols-1 gap-4 xl:grid-cols-[1.15fr_1fr]">
                  <Section title="Campaign" hint="Everything Google will receive for the campaign itself."
                    right={<Button variant="ghost" size="sm" className="h-7 px-2 text-2xs" onClick={() => setStep('campaign')}>Edit</Button>}>
                    <div className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
                      <Fact label="Name" value={name || '—'} wide />
                      <Fact label="App" value={appId || '—'} sub={storeLabel(store)} mono />
                      <Fact label="Goal" value={goalInfo.short} sub={goalInfo.label} />
                      <Fact label={goalInfo.cpaLabel} value={targetCpa ? `${targetCpa} ${currency ?? ''}` : '—'} />
                      <Fact label="Daily budget" value={budget ? `${budget} ${currency ?? ''}` : '—'} sub={budget ? `≈ ${(Number(budget) * 30.4).toFixed(0)} ${currency ?? ''} / month` : undefined} />
                      <Fact label="Schedule" value={startDate ? `${startDate} → ${endDate || 'no end'}` : `Starts when enabled${endDate ? ` → ends ${endDate}` : ' · no end date'}`} wide />
                    </div>
                    <div className="mt-4 space-y-3 border-t border-border pt-3">
                      <div>
                        <div className="mb-1.5 flex items-baseline justify-between">
                          <span className="text-2xs font-semibold uppercase tracking-wide text-text-muted">Locations</span>
                          <span className="text-2xs text-text-muted">{locations.length ? `${locations.length} selected` : 'Worldwide'}</span>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {locations.length === 0 && <span className="rounded-full bg-surface-alt px-2.5 py-1 text-xs text-text-secondary">Every country Google serves</span>}
                          {locations.slice(0, 14).map((l) => <span key={l.id} className="rounded-full border border-border bg-surface px-2.5 py-1 text-xs text-text-primary">{l.name}</span>)}
                          {locations.length > 14 && <span className="rounded-full bg-surface-alt px-2.5 py-1 text-xs text-text-muted">+{locations.length - 14} more</span>}
                        </div>
                      </div>
                      <div>
                        <div className="mb-1.5 text-2xs font-semibold uppercase tracking-wide text-text-muted">Languages</div>
                        <div className="flex flex-wrap gap-1.5">
                          {languages.length === 0
                            ? <span className="rounded-full bg-surface-alt px-2.5 py-1 text-xs text-text-secondary">All languages</span>
                            : languages.map((l) => <span key={l.id} className="rounded-full border border-border bg-surface px-2.5 py-1 text-xs text-text-primary">{l.name}</span>)}
                        </div>
                      </div>
                    </div>
                  </Section>

                  <Section title="Ad group & ad" hint="One ad group, one ad — Google assembles the placements from these pieces."
                    right={<Button variant="ghost" size="sm" className="h-7 px-2 text-2xs" onClick={() => setStep('creative')}>Edit</Button>}>
                    <div className="flex items-center gap-2 rounded-lg bg-surface-alt/60 px-3 py-2">
                      <Layers size={14} className="shrink-0 text-text-muted" />
                      <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-text-primary">{groupName || 'Untitled ad group'}</span>
                      <span className="shrink-0 text-2xs text-text-muted">{cleanHeadlines.length} headlines · {cleanDescriptions.length} descriptions · {readyImages.length} image{readyImages.length === 1 ? '' : 's'} · {validVideos.length} video{validVideos.length === 1 ? '' : 's'}</span>
                    </div>
                    <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <div>
                        <div className="mb-1 text-2xs font-semibold uppercase tracking-wide text-text-muted">Headlines</div>
                        <ul className="space-y-1">
                          {cleanHeadlines.map((h) => <li key={h} className="flex items-baseline justify-between gap-2 text-[13px] text-text-primary"><span className="truncate">{h}</span><span className="shrink-0 text-2xs tabular-nums text-text-muted">{h.length}/30</span></li>)}
                          {cleanHeadlines.length === 0 && <li className="text-xs text-danger">None yet</li>}
                        </ul>
                      </div>
                      <div>
                        <div className="mb-1 text-2xs font-semibold uppercase tracking-wide text-text-muted">Descriptions</div>
                        <ul className="space-y-1">
                          {cleanDescriptions.map((d) => <li key={d} className="text-xs leading-snug text-text-secondary">{d}</li>)}
                          {cleanDescriptions.length === 0 && <li className="text-xs text-danger">None yet</li>}
                        </ul>
                      </div>
                    </div>
                    <div className="mt-3">
                      <div className="mb-1 text-2xs font-semibold uppercase tracking-wide text-text-muted">Images & videos</div>
                      {readyImages.length === 0 && validVideos.length === 0 ? (
                        <p className="text-xs text-text-muted">None added — Google will still build ads from the store listing, but images and a video usually lift results a lot.</p>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {readyImages.map((im) => (
                            <div key={im.id} className="relative h-14 overflow-hidden rounded-lg border border-border bg-surface-alt" style={{ width: im.width && im.height ? Math.round(56 * Math.max(0.6, Math.min(im.width / im.height, 2))) : 56 }}>
                              <img src={im.url} alt="" className="h-full w-full object-cover" />
                              {im.shape && <span className="absolute bottom-0.5 left-0.5 rounded bg-black/70 px-1 text-[9px] text-white">{im.shape}</span>}
                            </div>
                          ))}
                          {validVideos.map((v) => <img key={v} src={`https://i.ytimg.com/vi/${v}/default.jpg`} alt="" className="h-14 w-24 rounded-lg border border-border object-cover" />)}
                        </div>
                      )}
                    </div>
                    <p className="mt-3 text-2xs text-text-muted">Google also pulls the icon, screenshots and description from the store listing itself.</p>
                  </Section>
                </div>

                {allIssues.length > 0 && (
                  <Section title={`${allIssues.length} thing${allIssues.length === 1 ? '' : 's'} to fix before publishing`}>
                    <ul className="space-y-1.5">
                      {allIssues.map((i) => (
                        <li key={i} className="flex items-start gap-2 text-[13px] text-danger">
                          <XCircle size={13} className="mt-0.5 shrink-0" /> {i}
                        </li>
                      ))}
                    </ul>
                  </Section>
                )}

                <Section title="Save campaign" hint="Creates everything on Google in order. The campaign and ad group start paused; app ads are always enabled, so nothing serves until you enable the campaign.">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2.5 text-sm text-text-primary">
                      <StepIcon s={pub.campaign} /> <span className="truncate">Campaign — {name || 'untitled'}</span>
                    </div>
                    <div className="flex items-center gap-2.5 pl-5 text-sm text-text-primary">
                      <StepIcon s={pub.adGroup} /> <span className="truncate">Ad group — {groupName}</span>
                    </div>
                    {readyImages.map((im) => (
                      <div key={im.id} className="flex items-center gap-2.5 pl-10 text-[13px] text-text-primary">
                        <StepIcon s={pub[`img:${im.url}`]} /> <span className="truncate">Image asset — {im.note}</span>
                      </div>
                    ))}
                    {validVideos.map((v) => (
                      <div key={v} className="flex items-center gap-2.5 pl-10 text-[13px] text-text-primary">
                        <StepIcon s={pub[`vid:${v}`]} /> <span className="truncate">Video asset — {v}</span>
                      </div>
                    ))}
                    <div className="flex items-center gap-2.5 pl-10 text-[13px] text-text-primary">
                      <StepIcon s={pub.ad} /> <span className="truncate">App ad — {cleanHeadlines[0] || 'untitled'}</span>
                    </div>
                  </div>

                  {pubError && (
                    <div className="mt-4">
                      <ErrorBox message={pubError} />
                      <p className="mt-1 text-2xs text-text-muted">Fix it and publish again — anything already created is kept, not repeated.</p>
                    </div>
                  )}

                  <div className="mt-5 flex justify-end gap-2">
                    <Button variant="secondary" onClick={() => navigate(APP_BASE)} disabled={publishing}>Cancel</Button>
                    <Button onClick={publish} loading={publishing} disabled={publishing || allIssues.length > 0} iconLeft={<Check size={15} />}>
                      {pubError ? 'Retry save' : 'Save paused campaign'}
                    </Button>
                  </div>
                </Section>
              </>
            )}
          </div>

          {/* AI image enhancement (App Promotion assistant) */}
          <Modal open={!!enhance} onClose={() => { if (!enhance?.busy) setEnhance(null); }} title="Enhance with AI" widthClass="max-w-2xl">
            {enhance && (
              <div className="space-y-4">
                <div className="flex gap-4">
                  <div className="w-40 shrink-0">
                    <img src={enhance.image.url} alt="" className="aspect-square w-full rounded-xl border border-border object-cover" />
                    <div className="mt-1 truncate text-2xs text-text-muted">Original · {enhance.image.note}</div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <Label>Pick a variant</Label>
                    <div className="mt-1.5 grid grid-cols-1 gap-2 sm:grid-cols-2">
                      {ENHANCE_VARIANTS.map((v) => (
                        <button key={v.value} type="button" disabled={enhance.busy} onClick={() => runEnhance(v.value)}
                          className={`rounded-xl border p-3 text-left transition-colors disabled:opacity-60 ${
                            enhance.variant === v.value ? 'border-primary bg-primary-soft/40' : 'border-border hover:bg-surface-alt'}`}>
                          <span className="block text-sm font-medium text-text-primary">{v.label}</span>
                          <span className="block text-2xs text-text-muted">{v.hint}</span>
                        </button>
                      ))}
                    </div>
                    <p className="mt-2 text-2xs text-text-muted">Uses your Gemini image model and counts toward the AI cost caps. The result is uploaded to your media library.</p>
                  </div>
                </div>
                {enhance.busy && <div className="flex items-center gap-2 text-xs text-text-muted"><Spinner size={14} className="text-primary" /> Generating…</div>}
                {enhance.error && <ErrorBox message={enhance.error} />}
                {enhance.result && (
                  <div className="flex flex-wrap items-start gap-4 rounded-xl border border-border p-3">
                    <img src={enhance.result.url} alt="" className="max-h-56 rounded-lg border border-border object-contain" />
                    <div className="min-w-0 flex-1 space-y-2">
                      <div className="text-sm font-medium text-text-primary">{enhance.result.label}</div>
                      {enhance.result.width && enhance.result.height && <div className="text-2xs text-text-muted">{enhance.result.width}×{enhance.result.height}</div>}
                      <div className="flex flex-wrap gap-2 pt-1">
                        <Button size="sm" onClick={() => acceptEnhance('add')} iconLeft={<Plus size={13} />}>Add as new image</Button>
                        <Button size="sm" variant="secondary" onClick={() => acceptEnhance('replace')}>Replace original</Button>
                      </div>
                    </div>
                  </div>
                )}
                <div className="flex justify-end">
                  <Button variant="ghost" size="sm" onClick={() => setEnhance(null)} disabled={enhance.busy}>Close</Button>
                </div>
              </div>
            )}
          </Modal>

          {/* live preview */}
          <div className="hidden w-[320px] shrink-0 xl:block">
            <div className="sticky top-24">
              <AppAdPreview ad={{
                store, appId, headlines, descriptions,
                iconUrl: aiCtx?.app.iconUrl, appName: aiCtx?.app.name,
                imageUrls: readyImages.map((i) => i.url),
                videoUrls: videos,
              }} />
            </div>
          </div>
        </div>
        )}
      </div>
    </>
  );
}
